import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: 'Are you sure?',
  message: '',
  confirmLabel: 'Continue',
  cancelLabel: 'Cancel',
  tone: 'default',
  showCancel: true,
};

// A bare string is the common case ("just show me this sentence"), so accept it
// as shorthand for { message }.
const normalise = (input) => (typeof input === 'string' ? { message: input } : (input || {}));

/**
 * Hosts the single app-wide confirm dialog and hands out a promise-based API in
 * place of window.confirm / window.alert:
 *
 *   const { confirm, alert } = useConfirm();
 *   if (!(await confirm('Delete this product?'))) return;
 *   await alert({ title: 'Assessment unlinked', message: '…' });
 *
 * Pass tone: 'danger' for anything irreversible — that's the only tone the card
 * draws differently (a red primary button).
 *
 * confirm() resolves true/false; alert() resolves once dismissed.
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);

  const settle = useCallback((value) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    if (resolve) resolve(value);
  }, []);

  const open = useCallback((options) => new Promise((resolve) => {
    // Nothing awaits two dialogs at once today, but if it ever happens don't
    // leave the first promise hanging forever.
    if (resolverRef.current) resolverRef.current(false);
    resolverRef.current = resolve;
    setState({ ...DEFAULTS, ...options });
  }), []);

  const confirm = useCallback(
    (input) => open(normalise(input)),
    [open],
  );

  const alert = useCallback(
    (input) => open({
      title: 'Heads up',
      confirmLabel: 'OK',
      ...normalise(input),
      showCancel: false,
    }).then(() => undefined),
    [open],
  );

  const value = useMemo(() => ({ confirm, alert }), [confirm, alert]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <ConfirmDialog
        {...(state || {})}
        open={!!state}
        onConfirm={() => settle(true)}
        onCancel={() => settle(false)}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside a <ConfirmProvider>');
  return ctx;
}

export default ConfirmProvider;
